import {
  visitNoteSourcesSchema,
  visitTranscriptFlagsSchema,
  visitTranscriptSegmentsSchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { referenceClient } from "@/lib/orpc/client";
import { useVisitScope } from "@/lib/visit-queries";

// v3.2 review layer (stage 2) reads: Sources for the main note and AI flags
// on the transcript. Both are read-only views; a failure just hides them.

// Cheap identity of the note text, so the sources are read again when the
// note changes (edit, assistant, regenerate).
const textKey = (text: string | null | undefined): string => {
  const value = text ?? "";
  let hash = 0;
  for (let i = 0; i < value.length; i++)
    hash = (Math.imul(hash, 31) + value.charCodeAt(i)) | 0;
  return `${value.length}:${hash}`;
};

export const useNoteSources = (
  visitPublicId: string | undefined,
  note: string | null | undefined,
  enabled: boolean,
) => {
  const scope = useVisitScope();
  return useQuery({
    queryKey: [
      "visit-note-sources",
      scope?.uid,
      scope?.accountPublicId,
      scope?.generation,
      visitPublicId,
      textKey(note),
    ],
    enabled: enabled && !!scope && !!visitPublicId && !!note?.trim(),
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) =>
      visitNoteSourcesSchema.parse(
        await referenceClient.getVisitNoteSources(
          { params: { public_id: String(visitPublicId) }, query: {} },
          { context: { expectedUserUid: scope?.uid }, signal },
        ),
      ),
  });
};

export const useTranscriptFlags = (
  visitPublicId: string | undefined,
  // Changes when the transcripts or the note change (status, text), so the
  // flags are read again after processing or an edit.
  revision: string,
  enabled: boolean,
) => {
  const scope = useVisitScope();
  return useQuery({
    queryKey: [
      "visit-transcript-flags",
      scope?.uid,
      scope?.accountPublicId,
      scope?.generation,
      visitPublicId,
      revision,
    ],
    enabled: enabled && !!scope && !!visitPublicId,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) =>
      visitTranscriptFlagsSchema.parse(
        await referenceClient.getVisitTranscriptFlags(
          { params: { public_id: String(visitPublicId) }, query: {} },
          { context: { expectedUserUid: scope?.uid }, signal },
        ),
      ),
  });
};

// Timed transcript segments for the drawer's segment view and player.
export const useTranscriptSegments = (
  visitPublicId: string | undefined,
  revision: string,
  enabled: boolean,
) => {
  const scope = useVisitScope();
  return useQuery({
    queryKey: [
      "visit-transcript-segments",
      scope?.uid,
      scope?.accountPublicId,
      scope?.generation,
      visitPublicId,
      revision,
    ],
    enabled: enabled && !!scope && !!visitPublicId,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) =>
      visitTranscriptSegmentsSchema.parse(
        await referenceClient.getVisitTranscriptSegments(
          { params: { public_id: String(visitPublicId) }, query: {} },
          { context: { expectedUserUid: scope?.uid }, signal },
        ),
      ),
  });
};

export { textKey as reviewTextKey };
